import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';
import FirebaseImageUpload from '../components/FirebaseImageUpload';
import { sendDirectWhatsApp, sendDirectSMS } from '../utils/directMessaging';
import AdminQuickPaymentModal from './AdminQuickPaymentModal';

const AdminCreateBill = ({ showMessage }) => {
    const [customer, setCustomer] = useState({ name: '', phone: '', address: '', pincode: '' });
    const [products, setProducts] = useState([{ name: '', packing: '', quantity: 1, price: 0 }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState('Home Delivery');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [storeBillUrl, setStoreBillUrl] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [existingCustomers, setExistingCustomers] = useState([]);
    
    // New billing fields
    const [discountAmount, setDiscountAmount] = useState(0);
    const [discountType, setDiscountType] = useState('₹');
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [promoCode, setPromoCode] = useState('');
    const [referredBy, setReferredBy] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [patientGender, setPatientGender] = useState('');

    // Notification states
    const [createdOrderInfo, setCreatedOrderInfo] = useState(null);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [isPushSending, setIsPushSending] = useState(false);
    const [promptNotification, setPromptNotification] = useState(true);

    const [showQuickPayment, setShowQuickPayment] = useState(false);

    const fetchCustomers = async () => {
        try {
            const data = await callApi('/admin/users');
            if (Array.isArray(data)) {
                setExistingCustomers(data.filter(u => u.role !== 'admin' && u.role !== 'super_admin'));
            }
        } catch (err) {
            console.error("Failed to load existing customers in AdminCreateBill", err);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    const selectExistingCustomer = (c) => {
        const cleanPhone = (c.mobile || '').replace(/^\+91/, '');
        setCustomer({
            name: c.name || '',
            phone: cleanPhone,
            address: c.address || (c.addresses && c.addresses[0]?.line1) || '',
            pincode: c.pincode || (c.addresses && c.addresses[0]?.pincode) || ''
        });
    };

    const handleCustomerChange = (e) => {
        setCustomer({ ...customer, [e.target.name]: e.target.value });
    };

    const handleProductChange = (index, e) => {
        const newProducts = [...products];
        let val = e.target.value;
        if (e.target.name === 'quantity' || e.target.name === 'price') {
            val = parseFloat(val) || 0;
        }
        newProducts[index][e.target.name] = val;
        setProducts(newProducts);

        if (e.target.name === 'name') {
            handleProductSearch(index, val);
        }
    };

    const handleProductSearch = async (index, searchTerm) => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            const newProducts = [...products];
            newProducts[index]._searchResults = [];
            newProducts[index]._showDropdown = false;
            setProducts(newProducts);
            return;
        }

        try {
            const token = localStorage.getItem('userToken');
            const response = await callApi(`/admin/products?limit=5&search=${encodeURIComponent(searchTerm)}`);
            if (response && response.products) {
                const newProducts = [...products];
                newProducts[index]._searchResults = response.products;
                newProducts[index]._showDropdown = true;
                setProducts(newProducts);
            }
        } catch (err) {
            console.error("Failed to search products", err);
        }
    };

    const selectProductResult = (index, productData) => {
        const newProducts = [...products];
        newProducts[index].name = productData.name || productData.productName;
        
        const packingStr = productData.packageVolume || productData.pack || '';
        newProducts[index].packing = packingStr;
        
        const match = packingStr.match(/(\d+)/);
        const totalUnits = match ? parseInt(match[1], 10) : 1;
        const mrp = productData.price || productData.mrp || 0;
        
        // Convert to per-unit pricing
        newProducts[index].quantity = totalUnits;
        newProducts[index].price = totalUnits > 0 ? parseFloat((mrp / totalUnits).toFixed(2)) : mrp;
        newProducts[index]._showDropdown = false;
        
        setProducts(newProducts);
    };

    const addProductRow = () => {
        setProducts([...products, { name: '', packing: '', quantity: 1, price: 0, _searchResults: [], _showDropdown: false }]);
    };


    const removeProductRow = (index) => {
        const newProducts = [...products];
        newProducts.splice(index, 1);
        setProducts(newProducts);
    };

    const subTotal = products.reduce((sum, p) => sum + (p.quantity * p.price), 0);
    const actualDiscount = discountType === '%' ? (subTotal * Number(discountAmount) / 100) : Number(discountAmount);
    const total = subTotal + (deliveryMethod === 'Home Delivery' ? Number(deliveryFee) : 0) - actualDiscount;

    const handleScanBill = async () => {
        if (!storeBillUrl) return;
        setIsScanning(true);
        try {
            const response = await callApi('/admin/scan-bill', 'POST', { imageUrl: storeBillUrl });
            if (response.success && response.data) {
                if (response.data.products && Array.isArray(response.data.products)) {
                    const currentValidProducts = products.filter(p => p.name.trim() !== '');
                    const scannedProducts = response.data.products.map(p => ({
                        name: p.name || '',
                        packing: p.packing || '',
                        looseUnits: '',
                        quantity: p.quantity || 1,
                        price: p.price || 0,
                        _searchResults: [],
                        _showDropdown: false
                    }));
                    setProducts(currentValidProducts.length > 0 ? [...currentValidProducts, ...scannedProducts] : scannedProducts);
                }
                showMessage('Bill scanned successfully!', 'success');
            } else {
                showMessage(response.message || 'Failed to scan bill.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Error scanning bill.', 'error');
        } finally {
            setIsScanning(false);
        }
    };

    const handleSendPush = async () => {
        if (!createdOrderInfo) return;
        setIsPushSending(true);
        try {
            const res = await callApi('/admin/send-notification', 'POST', {
                phone: createdOrderInfo.customerPhone,
                userId: createdOrderInfo.userId,
                title: 'Medicine Bill Ready — QwickMed',
                body: `Your bill for ₹${createdOrderInfo.total.toFixed(2)} is ready! Tap to pay online.`,
                data: { orderId: createdOrderInfo.orderId }
            });
            showMessage(res.message || 'Push notification dispatched!', res.success ? 'success' : 'info');
        } catch (err) {
            showMessage(err.message || 'Failed to send push notification.', 'error');
        } finally {
            setIsPushSending(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!customer.name || !customer.phone) {
            showMessage('Customer Name and Phone are required.', 'error');
            return;
        }
        if (deliveryMethod === 'Home Delivery' && (!customer.address || !customer.pincode)) {
            showMessage('Address and Pincode are required for Home Delivery.', 'error');
            return;
        }

        const validProducts = products.filter(p => p.name.trim() !== '');
        if (validProducts.length === 0) {
            showMessage('Please add at least one product with a name.', 'error');
            return;
        }

        setIsSubmitting(true);

        const cleanMobile = customer.phone.trim().replace(/\D/g, '').slice(-10);
        const formattedMobile = '+91' + cleanMobile;

        try {
            const alreadyExists = existingCustomers.some(u => {
                const uPhone = (u.mobile || '').replace(/\D/g, '').slice(-10);
                return uPhone === cleanMobile;
            });

            if (!alreadyExists && cleanMobile.length === 10) {
                await callApi('/admin/users/create-customer', 'POST', {
                    name: customer.name.trim(),
                    mobile: cleanMobile,
                    address: customer.address ? customer.address.trim() : '',
                    pincode: customer.pincode ? customer.pincode.trim() : ''
                });
            }
        } catch (custErr) {
            console.log("Customer save handled:", custErr);
        }

        const orderData = {
            customerName: customer.name.trim(),
            customerPhone: formattedMobile,
            customerAddress: customer.address ? customer.address.trim() : '',
            customerPincode: customer.pincode ? customer.pincode.trim() : '',
            deliveryMethod: deliveryMethod,
            paymentMethod: paymentMethod,
            deliveryAddress: deliveryMethod === 'Home Delivery' ? `${customer.address} - ${customer.pincode}` : 'Store Pickup',
            items: validProducts.map((p, idx) => ({
                productId: `manual-${Date.now()}-${idx}`,
                name: p.name,
                packing: p.packing,
                quantity: p.quantity,
                price: p.price,
                isCustom: true
            })),
            subTotal: subTotal,
            total: total,
            discountApplied: actualDiscount,
            deliveryFee: deliveryMethod === 'Home Delivery' ? deliveryFee : 0,
            promoCode: promoCode,
            referredBy: referredBy || undefined,
            patientAge: patientAge ? parseInt(patientAge) : undefined,
            patientGender: patientGender || undefined,
            isManualBill: true,
            storeBillUrl: storeBillUrl
        };

        try {
            const response = await callApi('/orders', 'POST', orderData);
            if (response.success) {
                showMessage(`Bill created & Customer saved in customer list! Order ID: ${response.order.orderId}`, 'success');
                fetchCustomers();
                
                const link = `${window.location.origin}/#pay/${response.order.orderId}`;
                const itemListText = validProducts.map(item => `• ${item.name} ${item.packing ? `(${item.packing}) ` : ''}x${item.quantity} = ₹${(item.price * item.quantity).toFixed(2)}`).join('\n');
                const defaultMsg = `Hi ${customer.name.trim() || 'Customer'}, your bill from QwickMed Pharmacy is ready!\n\nOrder ID: #${response.order.orderId}\nTotal Amount: ₹${total.toFixed(2)}\n\nBill Details:\n${itemListText}\n\nPlease pay securely using this link: ${link}`;

                const orderDetails = {
                    orderId: response.order.orderId,
                    userId: response.order.userId,
                    customerName: customer.name.trim(),
                    customerPhone: formattedMobile,
                    total: total,
                    items: validProducts,
                    link: link,
                    message: defaultMsg
                };

                setCreatedOrderInfo(orderDetails);
                if (promptNotification) {
                    setShowNotifyModal(true);
                }

                try {
                    navigator.clipboard.writeText(defaultMsg);
                } catch (err) {}

                setCustomer({ name: '', phone: '', address: '', pincode: '' });
                setProducts([{ name: '', packing: '', looseUnits: '', quantity: 1, price: 0, _searchResults: [], _showDropdown: false }]);
                setStoreBillUrl(null);
            } else {
                showMessage(response.message || 'Failed to create bill.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Error connecting to server.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in relative">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
                <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2.5">
                    <i className="fas fa-file-invoice text-sky-500"></i> Create Manual Bill
                </h2>
                <button 
                    type="button" 
                    onClick={() => setShowQuickPayment(true)}
                    className="bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 font-bold py-2 px-4 rounded-xl transition-all shadow-sm flex items-center gap-2 text-sm"
                >
                    <i className="fas fa-link"></i> Quick Payment Link
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                <div className="bg-slate-50 p-3.5 sm:p-6 rounded-2xl border border-slate-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3 mb-4">
                        <div>
                            <h3 className="text-base sm:text-lg font-bold text-slate-700 flex items-center gap-2">
                                <i className="fas fa-user-circle text-sky-500"></i> Customer Details
                            </h3>
                            <p className="text-[11px] text-slate-400 font-medium">New customers are automatically saved directly into the Customer List.</p>
                        </div>
                        {existingCustomers.length > 0 && (
                            <div className="w-full sm:w-72">
                                <select
                                    onChange={(e) => {
                                        const found = existingCustomers.find(u => u._id === e.target.value);
                                        if (found) selectExistingCustomer(found);
                                    }}
                                    defaultValue=""
                                    className="w-full text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-xl px-3 py-2 focus:ring-2 focus:ring-sky-400 outline-none shadow-sm cursor-pointer"
                                >
                                    <option value="" disabled>🔍 Pick Existing Customer ({existingCustomers.length})...</option>
                                    {existingCustomers.map(u => (
                                        <option key={u._id} value={u._id}>
                                            {u.name || 'Customer'} — {u.mobile}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Patient's Full Name *</label>
                            <input type="text" name="name" value={customer.name} onChange={handleCustomerChange} required className="w-full border-slate-200 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all text-sm font-semibold" placeholder="John Doe" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Contact Number *</label>
                            <input type="tel" name="phone" value={customer.phone} onChange={handleCustomerChange} required className="w-full border-slate-200 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all text-sm font-semibold" placeholder="10-digit mobile" />
                        </div>
                        
                        <div>
                            <label className="text-xs font-bold text-violet-500 uppercase tracking-wider mb-1 block">Referred By <span className="normal-case font-normal">(optional)</span></label>
                            <input type="text" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} className="w-full border-slate-200 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 bg-violet-50 focus:ring-2 focus:ring-violet-400 focus:border-transparent outline-none transition-all text-sm font-semibold text-violet-800 placeholder-violet-300" placeholder="Doctor / Person" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-rose-500 uppercase tracking-wider mb-1 block">Patient Info <span className="normal-case font-normal">(optional)</span></label>
                            <div className="flex gap-2">
                                <input type="number" min="1" max="120" placeholder="Age" value={patientAge} onChange={(e) => setPatientAge(e.target.value)} className="w-20 border-slate-200 rounded-xl px-2 py-2 sm:py-2.5 bg-rose-50 focus:ring-2 focus:ring-rose-400 focus:border-transparent outline-none transition-all text-sm font-semibold text-rose-800 placeholder-rose-300 text-center" />
                                <select value={patientGender} onChange={(e) => setPatientGender(e.target.value)} className="flex-1 border-slate-200 rounded-xl px-2 py-2 sm:py-2.5 bg-rose-50 focus:ring-2 focus:ring-rose-400 focus:border-transparent outline-none transition-all text-sm font-semibold text-rose-800 cursor-pointer">
                                    <option value="">Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="sm:col-span-2 mt-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Delivery Method</label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="deliveryMethod" value="Home Delivery" checked={deliveryMethod === 'Home Delivery'} onChange={(e) => setDeliveryMethod(e.target.value)} className="w-4 h-4 text-sky-600 focus:ring-sky-500 cursor-pointer" />
                                    <span className="text-xs sm:text-sm font-semibold text-slate-700">Home Delivery</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="deliveryMethod" value="Store Pickup" checked={deliveryMethod === 'Store Pickup'} onChange={(e) => setDeliveryMethod(e.target.value)} className="w-4 h-4 text-sky-600 focus:ring-sky-500 cursor-pointer" />
                                    <span className="text-xs sm:text-sm font-semibold text-slate-700">Store Pickup</span>
                                </label>
                            </div>
                        </div>

                        <div className="sm:col-span-2 mt-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Payment Method</label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="paymentMethod" value="Cash" checked={paymentMethod === 'Cash'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                                    <span className="text-xs sm:text-sm font-semibold text-slate-700">Cash / COD</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="paymentMethod" value="UPI" checked={paymentMethod === 'UPI'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                                    <span className="text-xs sm:text-sm font-semibold text-slate-700">Online / UPI</span>
                                </label>
                            </div>
                        </div>

                        {deliveryMethod === 'Home Delivery' && (
                            <>
                                <div className="sm:col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Delivery Address *</label>
                                    <textarea name="address" value={customer.address} onChange={handleCustomerChange} required rows="2" className="w-full border-slate-200 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all resize-none text-sm" placeholder="House no, Street, Area, City" />
                                </div>
                                <div className="sm:col-span-2 sm:w-1/2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Pincode *</label>
                                    <input type="text" name="pincode" value={customer.pincode} onChange={handleCustomerChange} required className="w-full border-slate-200 rounded-xl px-3.5 sm:px-4 py-2 sm:py-2.5 bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all text-sm font-semibold" placeholder="e.g. 695125" maxLength="6" />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="bg-slate-50 p-3.5 sm:p-6 rounded-2xl border border-slate-100">
                    <div className="flex justify-between items-center mb-3 sm:mb-4 border-b border-slate-200 pb-2">
                        <h3 className="text-base sm:text-lg font-bold text-slate-700">Product Items</h3>
                        <button type="button" onClick={addProductRow} className="text-xs sm:text-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-bold px-2.5 sm:px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
                            <i className="fas fa-plus"></i> Add Item
                        </button>
                    </div>

                    <div className="space-y-3">
                        {products.map((product, index) => (
                            <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-100 relative group">
                                <div className="grid grid-cols-4 sm:grid-cols-12 gap-3 sm:gap-4 items-start w-full">
                                    <div className="col-span-4 sm:col-span-5 relative">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Item Name</label>
                                        <input type="text" name="name" value={product.name} onChange={(e) => { handleProductChange(index, e); handleProductSearch(index, e.target.value); }} onFocus={() => { if (product._searchResults && product._searchResults.length > 0) { const newProducts = [...products]; newProducts[index]._showDropdown = true; setProducts(newProducts); } }} onBlur={() => setTimeout(() => { const newProducts = [...products]; if(newProducts[index]) newProducts[index]._showDropdown = false; setProducts(newProducts); }, 200)} className="w-full border-slate-200 rounded-lg px-3 py-2 text-xs sm:text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sky-400 outline-none placeholder-slate-300 font-bold text-slate-700" placeholder="Type to search..." autoComplete="off" required />
                                        {product._showDropdown && product._searchResults && product._searchResults.length > 0 && (
                                            <div className="absolute z-50 w-full mt-1">
                                                <ul className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                                                    {product._searchResults.map((res, i) => (
                                                        <li key={i} onMouseDown={() => selectProductResult(index, res)} className="px-3 py-2 hover:bg-sky-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                            <div className="font-bold text-xs text-slate-800">{res.name || res.productName}</div>
                                                            <div className="text-[10px] text-slate-500 flex justify-between">
                                                                <span>{res.packageVolume || res.pack || ''}</span>
                                                                <span className="text-sky-600 font-bold">₹{res.price || res.mrp}</span>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-2 sm:col-span-3">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Packing</label>
                                        <input type="text" name="packing" value={product.packing} onChange={(e) => handleProductChange(index, e)} className="w-full border-slate-200 rounded-lg px-3 py-2 text-xs sm:text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sky-400 outline-none" placeholder="e.g. 15 Tabs" />
                                    </div>
                                    <div className="col-span-1 sm:col-span-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Qty (Units)</label>
                                        <input type="number" min="0.01" step="any" name="quantity" value={product.quantity} onChange={(e) => handleProductChange(index, e)} className="w-full border-slate-200 rounded-lg px-3 py-2 text-xs sm:text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sky-400 outline-none font-bold text-sky-700" required />
                                    </div>
                                    <div className="col-span-1 sm:col-span-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Per-Unit Price (₹)</label>
                                        <input type="number" min="0" step="0.01" name="price" value={product.price} onChange={(e) => handleProductChange(index, e)} className="w-full border-slate-200 rounded-lg px-3 py-2 text-xs sm:text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sky-400 outline-none font-bold text-slate-700" required />
                                    </div>
                                </div>
                                {products.length > 1 && (
                                    <button type="button" onClick={() => removeProductRow(index)} className="text-red-400 hover:text-red-600 p-2 transition-colors self-end sm:self-center" title="Remove Item">
                                        <i className="fas fa-trash text-xs"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Summary & Submit */}
                <div className="bg-slate-50 p-3.5 sm:p-6 rounded-2xl border border-slate-100">
                    <h3 className="text-base sm:text-lg font-bold text-slate-700 mb-2 border-b border-slate-200 pb-2">Attach Store Bill (Optional)</h3>
                    <p className="text-xs sm:text-sm text-slate-500 mb-3">You can photograph the physical store bill for customer verification.</p>
                    {storeBillUrl ? (
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                                <i className="fas fa-check text-emerald-500 text-xl"></i>
                            </div>
                            <p className="text-xs sm:text-sm font-bold text-emerald-600 mb-2">Bill Uploaded Successfully</p>
                            <div className="flex gap-4 mb-2">
                                <button type="button" onClick={handleScanBill} disabled={isScanning} className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-bold py-2 px-5 rounded-xl shadow-sm transition-colors flex items-center gap-2 text-xs sm:text-sm">
                                    {isScanning ? <><i className="fas fa-spinner fa-spin"></i> Scanning...</> : <><i className="fas fa-magic"></i> Auto-fill with AI</>}
                                </button>
                            </div>
                            <button type="button" onClick={() => setStoreBillUrl(null)} className="text-xs text-red-500 font-bold underline">Remove and upload new</button>
                        </div>
                    ) : (
                        <FirebaseImageUpload onUploadSuccess={(url) => setStoreBillUrl(url)} folder="store_bills" />
                    )}
                </div>

                {/* Notification Options Setting */}
                <div className="bg-slate-50 p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0 text-sm font-extrabold shadow-inner">
                            <i className="fas fa-bell"></i>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm font-bold text-slate-800">Customer Notification Options</p>
                            <p className="text-[11px] text-slate-500 font-medium">Prompt WhatsApp, SMS & Push notification actions immediately after bill is created</p>
                        </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                            type="checkbox"
                            checked={promptNotification}
                            onChange={(e) => setPromptNotification(e.target.checked)}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500 shadow-inner"></div>
                    </label>
                </div>

                <div className="bg-sky-50 p-4 sm:p-6 rounded-2xl border-2 border-sky-100 flex flex-col gap-4">
                    <div className="flex flex-col gap-2 w-full">
                        {/* Subtotal */}
                        <div className="flex justify-between items-center py-2 border-b border-sky-200/50">
                            <span className="text-sky-700 font-bold text-sm">Subtotal</span>
                            <span className="font-black text-sky-800">₹{subTotal.toFixed(2)}</span>
                        </div>
                        
                        {/* Delivery Fee (Only if Home Delivery) */}
                        {deliveryMethod === 'Home Delivery' && (
                            <div className="flex justify-between items-center py-2 border-b border-sky-200/50">
                                <span className="text-sky-700 font-bold text-sm">Delivery Fee</span>
                                <div className="flex items-center gap-1">
                                    <span className="text-sky-700 font-bold">₹</span>
                                    <input type="number" min="0" step="1" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} className="w-16 border border-sky-200 bg-white rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-sky-400 font-bold text-sky-800" />
                                </div>
                            </div>
                        )}

                        {/* Discount */}
                        <div className="flex justify-between items-center py-2 border-b border-sky-200/50">
                            <span className="text-emerald-600 font-bold text-sm">Discount (-)</span>
                            <div className="flex items-center gap-1 bg-white border border-emerald-200 rounded p-0.5 shadow-sm">
                                <select 
                                    value={discountType} 
                                    onChange={(e) => setDiscountType(e.target.value)}
                                    className="bg-emerald-50 text-emerald-700 text-xs font-bold px-1 py-1 rounded cursor-pointer outline-none border-none focus:ring-0"
                                >
                                    <option value="₹">₹</option>
                                    <option value="%">%</option>
                                </select>
                                <input 
                                    type="number" 
                                    min="0" 
                                    step={discountType === '%' ? '0.1' : '1'} 
                                    value={discountAmount} 
                                    onChange={(e) => setDiscountAmount(e.target.value)} 
                                    className="w-16 border-none bg-transparent px-1 py-1 text-sm text-right focus:outline-none focus:ring-0 font-bold text-emerald-700" 
                                />
                            </div>
                        </div>

                        {/* Promo Code */}
                        <div className="flex justify-between items-center py-2 border-b border-sky-200/50">
                            <span className="text-sky-700 font-bold text-sm">Promo/Note</span>
                            <input type="text" placeholder="e.g. WELCOME10" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="w-32 border border-sky-200 bg-white rounded px-2 py-1 text-sm text-right uppercase focus:outline-none focus:ring-2 focus:ring-sky-400 font-bold text-sky-800" />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center pt-2 gap-4">
                        <div className="w-full sm:w-auto text-left">
                            <p className="text-xs sm:text-sm font-bold text-sky-700 uppercase tracking-wider mb-0.5">Grand Total</p>
                            <p className="text-2xl sm:text-4xl font-black text-sky-600">₹{total.toFixed(2)}</p>
                        </div>
                    <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white font-extrabold py-3 sm:py-4 px-6 sm:px-10 rounded-xl transition-all shadow-lg shadow-sky-500/25 flex items-center justify-center gap-2 text-sm sm:text-base">
                        {isSubmitting ? (
                            <><i className="fas fa-spinner fa-spin"></i> Creating Bill...</>
                        ) : (
                            <><i className="fas fa-check-circle"></i> Create Bill</>
                        )}
                    </button>
                </div>
            </div>
            </form>

            {/* Bill Created & Notification Options Modal */}
            {showNotifyModal && createdOrderInfo && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-2 sm:p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                        {/* Header */}
                        <div className="bg-slate-50 px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center text-lg font-extrabold shrink-0 shadow-md shadow-emerald-500/20">
                                    <i className="fas fa-check"></i>
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-xl font-black text-slate-800">Bill Created Successfully!</h3>
                                    <p className="text-xs text-emerald-700 font-semibold">Customer saved to customer list</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowNotifyModal(false)}
                                className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors shrink-0"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
                            {/* Order Summary Card */}
                            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex flex-wrap justify-between items-center gap-2">
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Customer</span>
                                    <p className="text-xs sm:text-sm font-bold text-slate-800">{createdOrderInfo.customerName} <span className="font-mono font-normal text-slate-500">({createdOrderInfo.customerPhone})</span></p>
                                </div>
                                <div className="text-right">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Amount</span>
                                    <p className="text-base sm:text-lg font-black text-emerald-600">₹{createdOrderInfo.total.toFixed(2)}</p>
                                </div>
                            </div>

                            {/* Notification Options Buttons */}
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                                    Choose How to Notify Customer:
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                    {/* WhatsApp */}
                                    <button
                                        type="button"
                                        onClick={() => sendDirectWhatsApp(createdOrderInfo.customerPhone, createdOrderInfo.message)}
                                        className="py-3 px-3 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-xs flex flex-col items-center justify-center gap-1.5 shadow-md shadow-emerald-500/20 transition-all transform hover:-translate-y-0.5"
                                        title="Open in WhatsApp Web / App"
                                    >
                                        <i className="fab fa-whatsapp text-xl"></i>
                                        <span>Send WhatsApp</span>
                                    </button>

                                    {/* Native SMS */}
                                    <button
                                        type="button"
                                        onClick={() => sendDirectSMS(createdOrderInfo.customerPhone, createdOrderInfo.message)}
                                        className="py-3 px-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs flex flex-col items-center justify-center gap-1.5 shadow-md shadow-sky-500/20 transition-all transform hover:-translate-y-0.5"
                                        title="Open in Native SMS App"
                                    >
                                        <i className="fas fa-comment-dots text-lg"></i>
                                        <span>Send Native SMS</span>
                                    </button>

                                    {/* App Push Notification */}
                                    <button
                                        type="button"
                                        onClick={handleSendPush}
                                        disabled={isPushSending}
                                        className="py-3 px-3 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-bold text-xs flex flex-col items-center justify-center gap-1.5 shadow-md shadow-purple-600/20 transition-all transform hover:-translate-y-0.5"
                                        title="Send Real-time Push to User Devices"
                                    >
                                        {isPushSending ? <i className="fas fa-spinner fa-spin text-lg"></i> : <i className="fas fa-bell text-lg"></i>}
                                        <span>App Push</span>
                                    </button>
                                </div>
                            </div>

                            {/* Editable Message Content */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Notification Message Content</label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            try {
                                                navigator.clipboard.writeText(createdOrderInfo.message);
                                                showMessage('Bill details copied to clipboard!', 'success');
                                            } catch (e) {}
                                        }}
                                        className="text-[11px] font-bold text-sky-600 hover:text-sky-700 flex items-center gap-1"
                                    >
                                        <i className="fas fa-copy"></i> Copy Text
                                    </button>
                                </div>
                                <textarea
                                    value={createdOrderInfo.message}
                                    onChange={(e) => setCreatedOrderInfo({ ...createdOrderInfo, message: e.target.value })}
                                    rows="5"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none font-medium text-xs resize-none"
                                ></textarea>
                            </div>

                            {/* Payment Link Box */}
                            <div className="bg-sky-50 p-3 rounded-xl border border-sky-100 flex items-center justify-between gap-2">
                                <div className="truncate">
                                    <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider block">Customer Payment Link</span>
                                    <p className="text-xs font-mono text-slate-700 truncate">{createdOrderInfo.link}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        try {
                                            navigator.clipboard.writeText(createdOrderInfo.link);
                                            showMessage('Payment link copied to clipboard!', 'success');
                                        } catch (e) {}
                                    }}
                                    className="px-3 py-1.5 bg-white border border-sky-200 text-sky-700 hover:bg-sky-50 text-xs font-bold rounded-lg shrink-0 transition-colors shadow-sm"
                                >
                                    Copy Link
                                </button>
                            </div>

                            {/* Payment QR Code */}
                            <div className="flex justify-center mt-4">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200 inline-block shadow-sm text-center">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Scan to Pay UPI</p>
                                    <img 
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`upi://pay?pa=vimalaxmistore@ybl&pn=Pharmacy&am=${createdOrderInfo.total.toFixed(2)}&cu=INR`)}`} 
                                        alt="UPI Payment QR" 
                                        className="w-40 h-40 mx-auto rounded-lg shadow-sm" 
                                    />
                                    <p className="text-[10px] text-slate-400 mt-2 font-mono">vimalaxmistore@ybl</p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="bg-slate-50 px-5 sm:px-6 py-3 border-t border-slate-100 flex justify-end shrink-0">
                            <button
                                type="button"
                                onClick={() => setShowNotifyModal(false)}
                                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {showQuickPayment && (
                <AdminQuickPaymentModal 
                    onClose={() => setShowQuickPayment(false)} 
                    showMessage={showMessage} 
                />
            )}
        </div>
    );
};

export default AdminCreateBill;
